<template>
  <div class="p-6 space-y-6">
    <header>
      <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
        <i class="far fa-file-arrow-down text-primary-500 mr-2" />
        Export Negative Keywords
      </h2>
      <p class="text-sm text-gray-500">
        Download the deduped, applied set for this Location's Google Ads campaign.
      </p>
      <p class="text-xs text-gray-400 mt-2">
        Pick a format below.
        <strong>Google Ads Bulk</strong> matches the format expected by Google Ads Editor / Bulk Upload.
        <strong>Generic CSV</strong> is a portable format with full source attribution.
      </p>
    </header>

    <fieldset class="space-y-2">
      <legend class="text-sm font-medium text-gray-900 dark:text-white">
        Format
      </legend>
      <label class="flex items-start gap-2">
        <input type="radio" v-model="format" value="google_ads_bulk" class="mt-1">
        <div>
          <div class="text-sm font-medium">Google Ads Bulk</div>
          <div class="text-xs text-gray-500">Editor / Bulk Upload format. Match types rendered as <code>Negative Broad/Phrase/Exact</code>.</div>
        </div>
      </label>
      <label class="flex items-start gap-2">
        <input type="radio" v-model="format" value="generic" class="mt-1">
        <div>
          <div class="text-sm font-medium">Generic CSV</div>
          <div class="text-xs text-gray-500"><code>keyword,match_type,source_list,scope</code> columns. Portable.</div>
        </div>
      </label>
    </fieldset>

    <p v-if="error" class="text-sm text-red-600">
      {{ error }}
    </p>

    <div class="flex justify-end gap-2">
      <button type="button" class="btn btn-secondary btn-sm" @click="$emit('on-close-panel')">
        Close
      </button>
      <button
        type="button"
        class="btn btn-primary btn-sm"
        :disabled="generating"
        @click="generate">
        <UiSpinner v-if="generating" :size="4" screen-reader="Generating CSV" class="inline-block mr-2 align-middle" />
        {{ generating ? 'Generating…' : 'Generate & Download' }}
      </button>
    </div>

    <section v-if="pastRuns.length > 0" class="border-t border-gray-200 dark:border-gray-700 pt-4">
      <h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-2">
        Past exports
      </h3>
      <ul class="space-y-2">
        <li v-for="run in pastRuns" :key="run.id" class="flex items-center justify-between text-sm">
          <div>
            <span class="font-medium">{{ run.format }}</span>
            <span class="text-gray-500"> · {{ run.keyword_count }} keywords · {{ formatDate(run.created_at) }}</span>
          </div>
          <a :href="run.download_url" target="_blank" rel="noopener"
            v-tooltip="'Re-download this CSV (opens in a new tab).'"
            class="text-primary-500 hover:text-primary-700">Re-download</a>
        </li>
      </ul>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import vTooltip from '~/javascript/directives/tooltip'
import UiSpinner from '~/javascript/components/ui/spinner.vue'
import { toastSuccess, toastError } from '~/javascript/utils/toast'

const props = defineProps({
  locationId: { type: String, required: true },
  csrfToken: { type: String, required: true }
})

// eslint-disable-next-line no-unused-vars
const emit = defineEmits(['on-close-panel'])

const format = ref('google_ads_bulk')
const generating = ref(false)
const error = ref(null)
const pastRuns = ref([])

const fetchPast = async() => {
  try {
    const res = await fetch(`/ajax/locations/${props.locationId}/negative_keyword_exports`, {
      headers: { Accept: 'application/json' }
    })
    if (!res.ok) return
    const data = await res.json()
    pastRuns.value = data.export_runs
  } catch (_) { /* silent */ }
}

const generate = async() => {
  generating.value = true
  error.value = null
  try {
    const res = await fetch(`/ajax/locations/${props.locationId}/negative_keyword_exports`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': props.csrfToken, 'Accept': 'application/json' },
      body: JSON.stringify({ format: format.value })
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error((data.errors || ['Export failed']).join(', '))
    }
    const data = await res.json()
    if (data.export_run.download_url) {
      window.open(data.export_run.download_url, '_blank', 'noopener')
    }
    toastSuccess(`Generated CSV with ${data.export_run.keyword_count} keyword${data.export_run.keyword_count === 1 ? '' : 's'}.`)
    await fetchPast()
  } catch (e) {
    error.value = e.message
    toastError(e)
  } finally {
    generating.value = false
  }
}

const formatDate = (iso) => new Date(iso).toLocaleString()

onMounted(fetchPast)
</script>
